/// <amd-module name="VSSUI/Components/HeaderCommandBar/HeaderCommandBar" />
import { ObservableLike } from "VSS/Core/Observable";
import { Button, IButtonProps } from "VSSUI/Button";
import { IMenuButtonProps, IMenuItem, MenuButton, MenuItemType } from "VSSUI/Menu";
import { OverflowButton, ResizeGroup } from "VSSUI/ResizeGroup";
import { css } from "VSSUI/Util";
import { FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { IFocusable } from "VSSUI/Utilities/Focus";
import * as React from "react";
import { CustomHeaderCommandBar } from "./CustomHeaderCommandBar";
import { IHeaderCommandBarItem, IHeaderCommandBarProps, IHeaderCommandBarWithFilterProps } from "./HeaderCommandBar.Props";
import { getFilterItem } from "./Items";

let headerCommandBarId = 1;

export interface IHeaderCommandBarFocusOptions {
    commandBarItemId: string;
}

export class HeaderCommandBar extends React.Component<IHeaderCommandBarProps> implements IFocusable<IHeaderCommandBarFocusOptions> {
    private moreButtonId: string;
    private overflowButtonRef = React.createRef<OverflowButton>();
    private buttonRefs: { [key: string]: React.RefObject<Button | MenuButton | OverflowButton> } = {};
    constructor(props: Readonly<IHeaderCommandBarProps>) {
        super(props);

        this.moreButtonId = props.moreButtonId || "header-command-bar-menu-button" + headerCommandBarId++;
    }
    public render() {
        const { items } = this.props;
        const sortedItems = items.sort((a: IHeaderCommandBarItem, b: IHeaderCommandBarItem) => {
            const aRank = a.rank ?? Number.MAX_VALUE;
            const bRank = b.rank ?? Number.MAX_VALUE;
            return aRank > bRank ? 1 : aRank < bRank ? -1 : 0;
        });

        let defaultElementId = "";
        this.buttonRefs = {};
        const buttonItems: JSX.Element[] = [];
        const overflowItems: IMenuItem[] = [];
        const extraItems: IMenuItem[] = [];
        const responsiveChildren: number[] = [];

        // Anything with important: true will be rendered as a button
        // Anything with important: false will be rendered in overflow
        // If buttonCount is supplied, that many buttons will be rendered into
        // a resizeGroup, and the rest will be overflow.  By default, buttonCount is 3.
        let buttonCount = this.props.buttonCount ?? 3;
        const isMenuBar = !items.length || items[0].role !== "button";
        sortedItems.forEach((value) => {
            const id = value.id;

            if (value.itemType === MenuItemType.Divider) {
                if (value.important) {
                    buttonItems.push(<div className="bolt-header-command-item-separator" key={id} />);
                } else {
                    extraItems.push(value);
                }
            } else {
                const buttonProps: IButtonProps | IMenuButtonProps = {
                    ariaChecked: ObservableLike.getValue(value.checked!),
                    ariaLabel: value.ariaLabel,
                    ariaRoleDescription: value.href ? "link" : "button",
                    ariaControls: value.ariaControls,
                    ariaDescribedBy: value.ariaDescribedBy,
                    ariaExpanded: value.ariaExpanded,
                    ariaHasPopup: value.ariaHasPopup,
                    ariaSetSize: value.ariaSetSize,
                    ariaPosInSet: value.ariaPosInSet,
                    ariaSelected: value.ariaSelected,
                    ariaPressed: value.ariaPressed,
                    className: css(value.className, "bolt-header-command-item-button"),
                    disabled: value.disabled,
                    href: value.href,
                    iconProps: value.iconProps,
                    id: id,
                    primary: value.isPrimary,
                    role: value.role || "menuitem",
                    subtle: value.subtle,
                    target: value.target,
                    text: value.text,
                    tooltipProps: value.tooltipProps
                };

                if (value.important === false || (value.important === undefined && buttonCount === 0)) {
                    extraItems.push(value);
                    return;
                } else {
                    if (value.important === undefined) {
                        responsiveChildren.push(buttonItems.length);
                        overflowItems.push(value);
                    }
                    buttonCount--;
                }

                let TagName: typeof Button | typeof MenuButton = Button;
                const ref = React.createRef<Button | MenuButton>();
                this.buttonRefs[id] = ref;

                if (value.subMenuProps) {
                    (buttonProps as IMenuButtonProps).contextualMenuProps = { menuProps: value.subMenuProps };
                    (buttonProps as IMenuButtonProps).hideDropdownIcon = value.hideDropdownIcon;
                    TagName = MenuButton;
                } else {
                    buttonProps.onClick = e => value.onActivate && value.onActivate(value, e);
                }

                if (!defaultElementId && !value.disabled) {
                    defaultElementId = id;
                }

                if (value.renderButton) {
                    buttonItems.push(value.renderButton(buttonProps));
                } else {
                    buttonItems.push(<TagName {...(buttonProps as IButtonProps & IMenuButtonProps)} key={id} ref={ref} />);
                }
            }
        });
        buttonItems.push(
            <OverflowButton
                className={css(this.props.overflowClassName, "bolt-header-command-item-button")}
                id={this.moreButtonId}
                key={this.moreButtonId}
                role="menuitem"
                ref={this.overflowButtonRef}
            />
        );
        this.buttonRefs[this.moreButtonId] = this.overflowButtonRef;

        // We will use a role of "menubar", unless the first item has a role of button.
        // This will be the case the close button in Panel Headers.

        if (items.length > 0) {
            return (
                <CustomHeaderCommandBar
                    className={this.props.className}
                    focusGroupProps={{ defaultElementId: defaultElementId || this.moreButtonId }}
                    role={isMenuBar ? "menubar" : undefined}
                >
                    <ResizeGroup
                        responsiveLayoutProps={{
                            reduceFlickering: this.props.reduceFlickering,
                            responsiveChildren: responsiveChildren.reverse()
                        }}
                        overflowMenuItems={overflowItems.reverse()}
                        extraItems={extraItems}
                        useAriaLabelForOverflow={this.props.useAriaLabelForOverflow}
                    >
                        <div className={css(
                                this.props.className,
                                "bolt-header-commandbar-button-group",
                                "flex-row flex-center flex-grow scroll-hidden rhythm-horizontal-8"
                            )}>
                            {buttonItems}
                        </div>
                    </ResizeGroup>
                </CustomHeaderCommandBar>
            );
        }
        return null;
    }

    public focus(options: IHeaderCommandBarFocusOptions) {
        const ref = this.buttonRefs[options.commandBarItemId];
        if (ref && ref.current) {
            ref.current.focus();
        }
    }
}

export interface IHeaderCommandBarWithFilterState {
    filterHasChanges: boolean;
}

export class HeaderCommandBarWithFilter
    extends React.Component<IHeaderCommandBarWithFilterProps, IHeaderCommandBarWithFilterState>
    implements IFocusable<IHeaderCommandBarFocusOptions>
{
    private headerCommandBarRef = React.createRef<HeaderCommandBar>();

    constructor(props: IHeaderCommandBarWithFilterProps) {
        super(props);
        this.state = { filterHasChanges: this.props.filter.hasChangesToReset() };
    }

    public componentDidMount() {
        this.props.filter.subscribe(this.onFilterChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount() {
        this.props.filter.unsubscribe(this.onFilterChanged, FILTER_CHANGE_EVENT);
    }

    public render() {
        const items = this.props.items ? [...this.props.items] : [];
        items.push(getFilterItem(this.onFilterClicked, this.state.filterHasChanges));

        return <HeaderCommandBar {...this.props} items={items} ref={this.headerCommandBarRef} />;
    }

    public focus(options: IHeaderCommandBarFocusOptions) {
        if (this.headerCommandBarRef.current) {
            this.headerCommandBarRef.current.focus(options);
        }
    }

    private onFilterClicked = () => {
        this.props.filterToggled.value = !this.props.filterToggled.value;
    };

    private onFilterChanged = () => {
        const hasChanges = this.props.filter.hasChangesToReset();
        if (hasChanges !== this.state.filterHasChanges) {
            this.setState({
                filterHasChanges: hasChanges
            });
        }
    };
}
